import { useCallback } from 'react';
import { useShowToast } from '@/hooks';
import {
  useFillScoreCardWithAiMutation,
  usePollScoreCardWithAiMutation,
} from '@/store/query/interviews';
import { type ScorecardType } from '@/types/interview';

const MAX_RETRIES = 25;
const POLLING_INTERVAL = 3000;

interface ScorecardFillWithAIParams {
  ats_application_id: string | number;
  scorecardId: string | number;
  interviewId: string | number;
  onSuccess: (scorecard: any | null, isError?: boolean) => void;
  description: string;
  onStart: () => void;
}

interface AIResponse {
  status: 'success' | 'queued' | 'failed';
  scorecard?: ScorecardType | null;
  error?: string;
}

interface FillAIResponse {
  data?: {
    msg_id: string;
  };
  error?: string[];
}

export const useScorecardFillWithAI = ({
  ats_application_id,
  scorecardId,
  interviewId,
  onSuccess,
  onStart,
}: ScorecardFillWithAIParams) => {
  const toast = useShowToast();
  const [fillWithAi, { isLoading: isFilling }] =
    useFillScoreCardWithAiMutation();
  const [pollAiResults, { isLoading: isPolling }] =
    usePollScoreCardWithAiMutation();

  const pollAiResultsHelper = useCallback(
    async (msg_id: string, retryCount = 0) => {
      if (retryCount >= MAX_RETRIES) {
        toast({
          title: 'AI processing timed out',
          description: 'Please try again later',
          status: 'error',
        });
        onSuccess(null, true);
        return;
      }

      try {
        const response = (await pollAiResults({
          applicantId: ats_application_id,
          scorecardId,
          msg_id,
        }).unwrap()) as AIResponse;

        if (response?.status === 'success') {
          toast({
            title: 'AI filled the scorecard successfully',
            status: 'success',
          });
          onSuccess(response.scorecard);
        } else if (response?.status === 'queued') {
          setTimeout(
            // eslint-disable-next-line react-hooks/immutability -- pre-existing; behavior preserved (extraction move)
            () => pollAiResultsHelper(msg_id, retryCount + 1),
            POLLING_INTERVAL
          );
        } else if (response.status === 'failed') {
          toast({
            title: 'AI processing failed',
            description: response.error || 'An unexpected error occurred',
            status: 'error',
          });
          onSuccess(null, true);
        }
      } catch (error: any) {
        toast({
          title: 'Error polling AI results',
          description: error.message || 'An unexpected error occurred',
          status: 'error',
        });
        onSuccess(null, true);
      }
    },
    [ats_application_id, scorecardId, pollAiResults, toast, onSuccess]
  );

  const handleFillWithAi = async ({
    description,
  }: {
    description: string;
  }): Promise<boolean> => {
    const response = (await fillWithAi({
      scorecardId,
      ats_interview_id: interviewId,
      applicantId: ats_application_id,
      user_description: description,
    })) as FillAIResponse;

    if ('error' in response) {
      toast({
        title: 'Error starting AI fill',
        description: response?.error?.[0],
        status: 'error',
      });
      return false;
    }

    const msg_id = response?.data?.msg_id;
    if (msg_id) {
      onStart();
      pollAiResultsHelper(msg_id);
      return true;
    }

    throw new Error('No message ID received from AI fill request');
  };

  return {
    handleFillWithAi,
    isFilling,
    isPolling,
  };
};
